import { Link } from "@/lib/router-compat";
import { useState, useMemo } from "react";
import { Search, Clock, Lock, Globe } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ToolCard from "@/components/ToolCard";
import { Input } from "@/components/ui/input";
import { tools, categories, comingSoonTools, type Tool } from "@/data/tools";

const Tools = () => {
  const featured = tools.filter((t) => t.featured);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return [] as Tool[];
    return tools.filter((t) =>
      [t.title, t.description, ...(t.features || [])]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [q]);
  const isSearching = q.length > 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title="Free Online Tools — Dev, SEO, Network, Design & Security"
        description="Free, privacy-first online tools for developers, designers and marketers — JSON, regex, JWT, HTTP, DNS, SEO, color, passwords and more. No sign-up."
        path="/tools"
        keywords="free online tools, free developer tools, free web tools, json formatter, regex tester, jwt decoder, http request builder, css generator, password generator, secure notes, network toolkit, dns lookup, seo analyzer, color palette generator, url shortener, privacy tools, no sign-up tools"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Free Tools", path: "/tools" },
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Free Web Developer Tools",
            url: "https://netius.com/tools",
            description: "Privacy-first, free online tools by NETIUS — data, API, frontend, performance, security and productivity.",
            publisher: { "@type": "Organization", name: "NETIUS", url: "https://netius.com" },
          },
          ...categories.map((cat) => ({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${cat.label} — Free Tools by NETIUS`,
            itemListElement: tools
              .filter((t) => t.category === cat.id)
              .map((t, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: t.external ? t.href : `https://netius.com${t.href}`,
                name: t.title,
              })),
          })),
        ]}
      />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="py-16 md:py-24 text-center">
          <div className="container px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">Free Tools</h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Enterprise-grade tools without the enterprise price tag. The tools we use daily to build world-class websites — now free for everyone.
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><Lock className="h-4 w-4" />100% private</span>
              <span className="flex items-center gap-2"><Globe className="h-4 w-4" />No sign-up</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" />Always free</span>
            </div>
          </div>
        </section>

        {/* Sticky category nav */}
        <nav
          aria-label="Tool categories"
          className="sticky top-16 z-30 border-y border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        >
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-2 overflow-x-auto py-3 scrollbar-none">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={`#${cat.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 min-h-11 rounded-full border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors whitespace-nowrap"
                >
                  <cat.icon className="h-4 w-4" />
                  {cat.label}
                </a>
              ))}
            </div>
          </div>
        </nav>

        {/* Featured / Search header */}
        <section className="py-12">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                  {isSearching ? `Search results (${filtered.length})` : "Most Popular"}
                </h2>
                <div className="flex items-center gap-3 sm:flex-shrink-0">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <Input
                      type="search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search tools…"
                      aria-label="Search tools"
                      className="pl-9 h-9"
                    />
                  </div>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">{tools.length} free tools</span>
                </div>
              </div>
              {isSearching ? (
                filtered.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    {filtered.map((t) => <ToolCard key={t.title} tool={t} />)}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-12">
                    No tools match "{query}". Try a different keyword.
                  </p>
                )
              ) : featured.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {featured.map((t) => <ToolCard key={t.title} tool={t} />)}
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {/* Categorized sections */}
        {!isSearching && categories.map((cat) => {
          const catTools = tools.filter((t) => t.category === cat.id);
          if (catTools.length === 0) return null;
          return (
            <section
              key={cat.id}
              id={cat.id}
              className="py-16 border-t border-border scroll-mt-32 odd:bg-muted/20"
            >
              <div className="container px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto">
                  <div className="mb-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium uppercase tracking-wider mb-3">
                      <cat.icon className="h-3.5 w-3.5" />
                      {cat.label}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{cat.label}</h2>
                    <p className="text-muted-foreground max-w-2xl">{cat.intro}</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    {catTools.map((t) => <ToolCard key={t.title} tool={t} />)}
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* Coming Soon */}
        <section className="py-16 border-t border-border bg-muted/30">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-sm text-muted-foreground uppercase tracking-wider mb-4">
                <Clock className="inline h-4 w-4 mr-1" />
                COMING SOON
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">More Tools on the Way</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We are constantly expanding the toolkit across data, API, design, performance and security categories.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto mb-10">
              {comingSoonTools.map((label) => (
                <span key={label} className="px-4 py-2 rounded-full border border-border bg-card text-sm text-muted-foreground">
                  {label}
                </span>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground">
              Have a tool you'd like to see?{" "}
              <Link to="/contact" className="text-foreground underline underline-offset-4 hover:text-primary transition-colors">
                Suggest one →
              </Link>
            </p>
          </div>
        </section>

        {/* Why Use Our Tools */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Why Use Our Tools?</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Built by NETIUS with the same standards that power our client websites — high-performance sites delivering millions of requests every month.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                { icon: Lock, title: "Privacy First", description: "We never log, track, or store your data. Everything runs in your browser — your information stays on your device, not ours." },
                { icon: Clock, title: "Always Available", description: "Built on the same infrastructure that powers our high-traffic client websites. Fast, reliable, and free whenever you need it." },
                { icon: Globe, title: "No Registration", description: "No sign-ups, no accounts, no ads, no paywalls. Just bookmark and go — every tool, every feature, completely free." },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 rounded-xl border border-border bg-card">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Tools CTA */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Need Custom Tools?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              We build custom web tools and applications for businesses across Central Florida and beyond. From idea to deployment, we deliver and support every tool we ship.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg border-2 border-foreground text-foreground hover:bg-foreground hover:text-background transition-all duration-300"
            >
              GET IN TOUCH
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Tools;